import { useCallback, useEffect, useState } from 'react';
import { Plus, Link2 } from 'lucide-react';
import Modal from '../ui/Modal';
import { api } from '../../lib/api';
import { Field, TextInput, TextArea, Select, MoneyInput, History, Money, Empty, eur, usd, fmtDate, today, btnCls, btn2Cls, cardCls } from './shared';
import { BooksChip } from './LedgerTab';

// International wires and other payments between CCS and Concria Oy, matched to the ledger
// lines they settle. Concria's invoices are paid by wire (CCS invoices are not factored).

const DIR = { to_concria: 'CCS paid Concria', from_concria: 'Concria paid CCS' };
const METHOD = { wire: 'International wire', ach: 'ACH', card: 'Card', offset: 'Offset (netted)', other: 'Other' };

export default function PaymentsTab({ me, posting, onChanged }) {
  const [payments, setPayments] = useState(null);
  const [lines, setLines] = useState([]);
  const [edit, setEdit] = useState(null);
  const [match, setMatch] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const [p, l] = await Promise.all([api.get('/intercompany/payments'), api.get('/intercompany/lines')]);
      setPayments(p.payments); setLines(l.lines); setError('');
    } catch (e) { setError(e.message); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const done = () => { setEdit(null); setMatch(null); load(); onChanged(); };
  const lineById = new Map(lines.map((l) => [l.id, l]));

  const unmatch = async (id) => {
    if (!window.confirm('Remove this match?')) return;
    try { await api.post(`/intercompany/allocations/${id}/void`, {}); done(); } catch (e) { setError(e.message); }
  };
  const post = async (p, allowBefore = false) => {
    try { await api.post(`/intercompany/payments/${p.id}/post-to-books`, { allow_before_cutover: allowBefore }); done(); } catch (e) {
      if (e.status === 409 && /cutover/.test(e.message) && window.confirm(`${e.message}\n\nPost anyway?`)) return post(p, true);
      setError(e.message);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-[var(--text-secondary)]">Record each wire, then match it to the invoices (ledger lines) it pays. Store no bank account numbers here.</p>
        <button className={btnCls} onClick={() => setEdit({})}><Plus size={14} /> Record payment</button>
      </div>
      {error && <p className="text-sm text-rose-600">{error}</p>}
      {!payments ? <p className="text-sm text-[var(--text-secondary)]">Loading...</p> : payments.length === 0 ? <Empty>No payments recorded.</Empty> : payments.map((p) => {
        const left = p.method === 'offset' ? 2 * p.amount_cents - p.allocated_cents : p.amount_cents - p.allocated_cents;
        return (
          <div key={p.id} className={cardCls}>
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div>
                <div className="text-xs text-[var(--text-secondary)]">{fmtDate(p.pay_date)} · {p.method === 'offset' ? 'Offset (no money moved)' : DIR[p.direction]} · {METHOD[p.method]} <BooksChip posting={posting} entryId={p.books_entry_id} date={p.pay_date} /></div>
                <div className="text-lg font-semibold"><Money cents={p.amount_cents} />{p.eur_cents ? <span className="ml-2 text-sm font-normal text-[var(--text-secondary)]">{eur(p.eur_cents)}</span> : null}</div>
                <div className="text-xs text-[var(--text-secondary)]">{[p.reference, p.beneficiary, p.invoice_refs].filter(Boolean).join(' · ')}{p.fee_cents ? ` · fee ${usd(p.fee_cents)}` : ''}</div>
                {p.note && <div className="mt-1 text-xs text-[var(--text-secondary)]">{p.note}</div>}
              </div>
              <div className="flex flex-wrap gap-2">
                {left > 0 && <button className={btn2Cls} onClick={() => setMatch(p)}><Link2 size={14} /> Match to lines</button>}
                <button className={btn2Cls} onClick={() => setEdit(p)}>Edit</button>
                {me.can_post && !p.books_entry_id && <button className={btn2Cls} onClick={() => post(p)}>Post to Books</button>}
              </div>
            </div>
            <div className="mt-2 text-xs">
              {p.allocations.length === 0 ? <span className="text-amber-700 dark:text-amber-400">Not matched to any line yet.</span> : (
                <ul className="space-y-1">
                  {p.allocations.map((a) => (
                    <li key={a.id} className="flex flex-wrap items-center gap-2">
                      <span>{usd(a.amount_cents)} to {lineById.get(a.line_id)?.description || 'a line'}</span>
                      <button className="text-rose-600" onClick={() => unmatch(a.id)}>remove</button>
                    </li>
                  ))}
                </ul>
              )}
              {left > 0 && p.allocations.length > 0 && <div className="mt-1 text-amber-700 dark:text-amber-400">{usd(left)} not matched to any line.</div>}
              {p.books_entry_id && <div className="mt-1 text-[var(--text-secondary)]">In Books (entry {p.books_entry_id.slice(0, 8)}).</div>}
            </div>
          </div>
        );
      })}
      {edit && <PaymentModal payment={edit.id ? edit : null} onClose={() => setEdit(null)} onSaved={done} />}
      {match && <MatchModal payment={match} lines={lines} onClose={() => setMatch(null)} onSaved={done} />}
    </div>
  );
}

const FIELDS = ['direction', 'pay_date', 'amount_cents', 'eur_cents', 'fx_rate', 'fee_cents', 'method', 'reference', 'beneficiary', 'invoice_refs', 'source_url', 'note'];

function PaymentModal({ payment, onClose, onSaved }) {
  const [f, setF] = useState(() => (payment ? Object.fromEntries(FIELDS.map((k) => [k, payment[k] ?? ''])) : { direction: 'to_concria', method: 'wire', pay_date: today(), fee_cents: 0 }));
  const [error, setError] = useState('');
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v }));
  const save = async () => {
    try {
      const body = {};
      for (const k of FIELDS) {
        const v = f[k];
        if (payment && (v ?? '') === (payment[k] ?? '')) continue;
        if (!payment && (v === '' || v == null)) continue;
        body[k] = v === '' ? null : v;
      }
      if (payment) await api.patch(`/intercompany/payments/${payment.id}`, { ...body, revision: payment.updated_at });
      else await api.post('/intercompany/payments', body);
      onSaved();
    } catch (e) { setError(e.message); }
  };
  const remove = async () => {
    if (!window.confirm('Delete this payment?')) return;
    try { await api.delete(`/intercompany/payments/${payment.id}`); onSaved(); } catch (e) { setError(e.message); }
  };
  return (
    <Modal title={payment ? 'Payment' : 'Record a payment'} onClose={onClose}>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label="Direction"><Select value={f.direction} onChange={set('direction')} options={DIR} /></Field>
        <Field label="Method"><Select value={f.method} onChange={set('method')} options={METHOD} /></Field>
        <Field label="Date"><TextInput type="date" value={f.pay_date} onChange={set('pay_date')} /></Field>
        <Field label="Amount that moved (USD)"><MoneyInput cents={f.amount_cents || null} onChange={set('amount_cents')} /></Field>
        <Field label="Amount (EUR, optional)"><MoneyInput cents={f.eur_cents || null} onChange={set('eur_cents')} /></Field>
        <Field label="FX (USD per EUR)"><TextInput value={f.fx_rate} onChange={set('fx_rate')} /></Field>
        <Field label="Bank fee (USD)"><MoneyInput cents={f.fee_cents || null} onChange={(v) => set('fee_cents')(v ?? 0)} /></Field>
        <Field label="Wire reference"><TextInput value={f.reference} onChange={set('reference')} /></Field>
        <Field label="Beneficiary (name only)"><TextInput value={f.beneficiary} onChange={set('beneficiary')} placeholder="Concria Oy" /></Field>
        <Field label="Invoices covered"><TextInput value={f.invoice_refs} onChange={set('invoice_refs')} /></Field>
        <Field label="Confirmation link" className="sm:col-span-2"><TextInput value={f.source_url} onChange={set('source_url')} placeholder="https://" /></Field>
        <Field label="Note" className="sm:col-span-2"><TextArea value={f.note} onChange={set('note')} rows={2} /></Field>
      </div>
      {error && <p className="mt-3 text-sm text-rose-600">{error}</p>}
      <div className="mt-4 flex gap-2">
        <button className={btnCls} onClick={save}>{payment ? 'Save' : 'Record'}</button>
        {payment && <button className={btn2Cls} onClick={remove}>Delete</button>}
      </div>
      {payment && <History table="payments" id={payment.id} />}
    </Modal>
  );
}

function MatchModal({ payment, lines, onClose, onSaved }) {
  const owed = payment.method === 'offset' ? null : payment.direction === 'to_concria' ? 'ccs' : 'concria';
  const open = lines.filter((l) => (!owed || l.owed_by === owed) && !l.memo_only && l.status !== 'written_off' && l.usd_cents != null && l.allocated_cents < l.usd_cents);
  const [lineId, setLineId] = useState(open[0]?.id || '');
  const [amt, setAmt] = useState(null);
  const [error, setError] = useState('');
  const save = async () => {
    try { await api.post('/intercompany/allocations', { payment_id: payment.id, line_id: lineId, ...(amt ? { amount_cents: amt } : {}) }); onSaved(); } catch (e) { setError(e.message); }
  };
  return (
    <Modal title="Match payment to a line" onClose={onClose}>
      {open.length === 0 ? <p className="text-sm">No open lines to match. Add the invoice on the Ledger tab first.</p> : (
        <>
        {payment.method === 'offset' && <p className="mb-2 text-xs text-[var(--text-secondary)]">An offset nets a line Concria owes against a line CCS owes. Match it to one line on each side, up to the offset amount each.</p>}
        <div className="space-y-3">
          <Field label="Line">
            <select className="w-full rounded-lg border border-[var(--border)] bg-white px-2 py-1.5 text-sm dark:bg-slate-800" value={lineId} onChange={(e) => setLineId(e.target.value)}>
              {open.map((l) => <option key={l.id} value={l.id}>{l.ccs_invoice_no || l.concria_invoice_no || ''} {l.description.slice(0, 60)} ({usd(l.usd_cents - l.allocated_cents)} open{owed ? '' : l.owed_by === 'ccs' ? ', CCS owes' : ', Concria owes'})</option>)}
            </select>
          </Field>
          <Field label="Amount (blank = as much as fits)"><MoneyInput cents={null} onChange={setAmt} /></Field>
          {error && <p className="text-sm text-rose-600">{error}</p>}
          <button className={btnCls} onClick={save} disabled={!lineId}>Match</button>
        </div>
        </>
      )}
    </Modal>
  );
}

